/**
 * Integrytas Group - Common Security Module (CSM)
 * RoleReconciliationModal — when a new-user request changes Job Description,
 * offer logical ways to combine the current role selection (S) with the old
 * job's defaults (O) and the new job's defaults (N). Pure set math lives in
 * src/lib/role_reconcile.ts; this is only the chooser UI, plus a "Customize"
 * free-checklist mode.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { applyRoleStrategy, reconcilePreview, type RoleReconcileStrategy } from '../../lib/role_reconcile';

export interface RoleReconciliationModalProps {
  roleLabels: Map<number, string>;
  current: number[];
  oldDefaults: number[];
  newDefaults: number[];
  onApply: (resultIds: number[]) => void;
  onCancel: () => void;
}

export function RoleReconciliationModal({
  roleLabels, current, oldDefaults, newDefaults, onApply, onCancel,
}: RoleReconciliationModalProps) {
  const { t } = useTranslation();
  // 'customize' is not a set-strategy — it's a free checklist mode.
  const [strategy, setStrategy] = useState<RoleReconcileStrategy | 'customize'>('swap');
  const [customSel, setCustomSel] = useState<Set<number>>(new Set());

  const oldSet = new Set(oldDefaults);
  const newSet = new Set(newDefaults);
  const label = (id: number) => roleLabels.get(id) ?? String(id);

  const STRATEGIES: { key: RoleReconcileStrategy | 'customize'; title: string; desc: string }[] = [
    { key: 'swap',       title: t('user_provision.role_reconcile.swap.title', 'Swap job roles'),            desc: t('user_provision.role_reconcile.swap.desc', "Remove the old job's roles, keep your manual picks, add the new job's roles.") },
    { key: 'replace',    title: t('user_provision.role_reconcile.replace.title', 'Replace all with new job'), desc: t('user_provision.role_reconcile.replace.desc', "Use only the new job's roles.") },
    { key: 'add',        title: t('user_provision.role_reconcile.add.title', "Add new job's roles"),          desc: t('user_provision.role_reconcile.add.desc', "Keep everything selected and add the new job's roles.") },
    { key: 'remove_old', title: t('user_provision.role_reconcile.remove_old.title', "Remove old job's roles only"), desc: t('user_provision.role_reconcile.remove_old.desc', "Strip the old job's roles and add nothing.") },
    { key: 'keep',       title: t('user_provision.role_reconcile.keep.title', 'Keep current'),                desc: t('user_provision.role_reconcile.keep.desc', "Ignore the new job's default roles.") },
    { key: 'customize',  title: t('user_provision.role_reconcile.customize.title', 'Customize…'),             desc: t('user_provision.role_reconcile.customize.desc', 'Tick exactly the roles you want for this user.') },
  ];

  // Switching INTO customize seeds the checklist from the currently-chosen
  // strategy's result, so the user starts from a sensible set and edits it.
  const chooseStrategy = (key: RoleReconcileStrategy | 'customize') => {
    if (key === 'customize' && strategy !== 'customize') {
      const seed = applyRoleStrategy(strategy, current, oldDefaults, newDefaults);
      setCustomSel(new Set(seed));
    }
    setStrategy(key);
  };

  // All role ids relevant to this decision (new defaults + old defaults + current),
  // for the customize checklist. Sorted ascending for a stable list.
  const customizeIds = [...new Set([...newDefaults, ...oldDefaults, ...current])].sort((a, b) => a - b);
  const toggleCustom = (id: number) => setCustomSel(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const result = strategy === 'customize'
    ? [...customSel].sort((a, b) => a - b)
    : applyRoleStrategy(strategy, current, oldDefaults, newDefaults);
  const preview = reconcilePreview(result, current);

  return (
    <Modal
      title={t('user_provision.role_reconcile.title', 'Update roles for the new job')}
      onClose={onCancel}
      width="lg"
      footer={
        <>
          <button onClick={onCancel} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">
            {t('common.cancel', 'Cancel')}
          </button>
          <button onClick={() => onApply(result)} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover">
            {t('user_provision.role_reconcile.apply', 'Apply')}
          </button>
        </>
      }
    >
      <div className="mb-3 grid grid-cols-2 gap-3 text-csm-sm">
        <div>
          <div className="mb-1 font-medium text-csm-ink-soft">{t('user_provision.role_reconcile.new_defaults', "New job's default roles")}</div>
          <ul className="list-disc pl-4 text-csm-ink-soft">
            {newDefaults.length === 0
              ? <li className="italic text-csm-watermark">{t('user_provision.role_reconcile.none', '(none)')}</li>
              : newDefaults.map(id => <li key={id}>{label(id)}</li>)}
          </ul>
        </div>
        <div>
          <div className="mb-1 font-medium text-csm-ink-soft">{t('user_provision.role_reconcile.current', 'Currently selected')}</div>
          <ul className="list-disc pl-4 text-csm-ink-soft">
            {current.length === 0
              ? <li className="italic text-csm-watermark">{t('user_provision.role_reconcile.none', '(none)')}</li>
              : current.map(id => (
                  <li key={id}>{label(id)}{oldSet.has(id) && <span className="ml-1 text-csm-2xs text-csm-muted">{t('user_provision.role_reconcile.from_old', '(from old job)')}</span>}</li>
                ))}
          </ul>
        </div>
      </div>

      <div className="space-y-1">
        {STRATEGIES.map(s => (
          <label key={s.key} className="flex cursor-pointer items-start gap-2 rounded border border-csm-border px-2 py-1.5 hover:bg-csm-surface-alt">
            <input type="radio" name="reconcile" checked={strategy === s.key} onChange={() => chooseStrategy(s.key)} className="mt-0.5 accent-gray-700" />
            <span>
              <span className="block text-csm-sm font-medium text-csm-ink">{s.title}</span>
              <span className="block text-csm-2xs text-csm-muted">{s.desc}</span>
            </span>
          </label>
        ))}
      </div>

      {strategy === 'customize' && (
        <div className="mt-2 max-h-48 overflow-auto rounded border border-csm-border">
          <table className="w-full text-csm-sm">
            <tbody>
              {customizeIds.map(id => (
                <tr key={id} className="cursor-pointer border-t border-csm-border-soft hover:bg-csm-surface-alt" onClick={() => toggleCustom(id)}>
                  <td className="w-6 px-1 py-1 text-center">
                    <input type="checkbox" readOnly checked={customSel.has(id)} className="h-3.5 w-3.5 accent-gray-700" />
                  </td>
                  <td className="px-2 py-1">{label(id)}</td>
                  <td className="px-2 py-1 text-right">
                    {newSet.has(id) && <span className="ml-1 rounded border border-csm-info-border bg-csm-info-soft px-1 text-csm-2xs text-csm-info">{t('user_provision.role_reconcile.tag_new', 'new job')}</span>}
                    {oldSet.has(id) && <span className="ml-1 rounded border border-csm-border bg-csm-surface-alt px-1 text-csm-2xs text-csm-muted">{t('user_provision.role_reconcile.tag_old', 'old job')}</span>}
                  </td>
                </tr>
              ))}
              {customizeIds.length === 0 && (
                <tr><td colSpan={3} className="px-2 py-3 text-center italic text-csm-watermark">{t('user_provision.role_reconcile.none', '(none)')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 rounded border border-csm-border bg-csm-surface-alt px-2 py-1 text-csm-2xs text-csm-ink-soft">
        {t('user_provision.role_reconcile.preview', 'Result: {{total}} roles ({{kept}} kept, {{added}} added)', preview)}
      </div>
    </Modal>
  );
}
